import { Routes } from '@angular/router';
import { authGuard } from './guards/auth/auth.guard';
import { BusinessEntityPage } from './pages/business-entity/business-entity.component';
import { BuyerAddressUploadPage } from './pages/buyer-data-upload/buyer-address-upload/buyer-address-upload.component';
import { BuyerUploadPage } from './pages/buyer-data-upload/buyer-upload/buyer-upload.component';
import { BuyerManagementPage } from './pages/buyer-management/buyer-management.component';
import { CurrencyConversionPage } from './pages/currency-conversion/currency-conversion.component';
import { DashboardPage } from './pages/dashboard/dashboard';
import { DeviceCareCenterPage } from './pages/device-care-center/device-care-center.component';
import { DownloadsPage } from './pages/downloads/download.component';
import { InventoryPage } from './pages/inventory/inventory.component';
import { LoginLandingPage } from './pages/login-landing/login-landing';
import { OrderDetailsPage } from './pages/order-details/order-details.component';
import { OrdersPage } from './pages/orders/orders.component';
import { PricingPage } from './pages/pricing/pricing.component';
import { ProductUploadPage } from './pages/product-upload/product-upload.component';
import { ProductPage } from './pages/product/product.component';
import { RegionSelectPage } from './pages/region-select/region-select.component';
import { SkuMappingPage } from './pages/sku-mapping/sku-mapping.component';
import { BuyerLoginUploadPage } from './pages/user-management/buyer-login-upload/buyer-login-upload.component';
import { UserFormPage } from './pages/user-management/user-form/user-form.component';
import { UserManagementPage } from './pages/user-management/user-management.component';
import { UsersPage } from './pages/user-management/users/users.component';
import { UserProfilePage } from './pages/user-profile/user-profile.component';
import { WarehouseDetailsPage } from './pages/warehouse-details/warehouse-details.component';
import { RmaConfigPage } from './pages/rma-config/rma-config.component';
import { RmaCreatePage } from './pages/rma-create/rma-create.component';
import { RmaDetailsPage } from './pages/rma-details/rma-details.component';
import { ApprovedRejectedPage } from './pages/approved-rejected/approved-rejected.component';
import { PendingApprovalPage } from './pages/pending-approval/pending-approval.component';
import { RmaSummaryPage } from './pages/rma-summary/rma-summary.component';
import { WarehouseManagementPage } from './pages/warehouse-management/warehouse-management.component';
import { WmsPage } from './pages/wms/wms.component';
import { UnauthorizedPage } from './pages/unauthorized/unauthorized.component';

export const routes: Routes = [
  {
    path: 'unauthorized',
    component: UnauthorizedPage,
    title: 'Access Denied',
    data: {
      hideFromNavigation: true,
      hideNavigation: true,
      hideApplicationChrome: true,
    },
  },
  {
    path: 'login/callback',
    component: LoginLandingPage,
    data: {
      hideFromNavigation: true,
      hideNavigation: true,
    },
  },
  {
    path: 'profile',
    component: UserProfilePage,
    canActivate: [authGuard],
    data: {
      hideFromNavigation: true,
    },
  },
  {
    path: 'select-region',
    component: RegionSelectPage,
    title: 'Region Picker',
    canActivate: [authGuard],
    data: {
      hideFromNavigation: true,
      featureSet: 'public',
      hideNavigation: true,
    },
  },
  {
    path: 'dashboard',
    component: DashboardPage,
    title: 'Dashboard',
    canActivate: [authGuard],
    data: { symbol: 'dashboard', featureSet: 'public' },
  },
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
    data: {
      hideFromNavigation: true,
      hideNavigation: true,
    },
  },
  {
    path: 'orders',
    component: OrdersPage,
    title: 'Orders',
    data: { symbol: 'list_alt', featureSet: 'order' },
    canActivate: [authGuard],
  },
  {
    path: 'orders/:orderNumber',
    component: OrderDetailsPage,
    title: 'Orders',
    data: {
      hideFromNavigation: true,
    },
    canActivate: [authGuard],
  },
  {
    path: 'catalogs',
    title: 'Catalogs',
    data: { symbol: 'auto_stories' },
    canActivate: [authGuard],
    children: [
      {
        path: '', // Empty path matches the parent route
        redirectTo: 'product', // Redirect to 'child1' by default
        pathMatch: 'full', // Ensure a full match for the empty path
      },
      {
        path: 'products',
        component: ProductPage,
        title: 'Products',
        data: {
          symbol: 'package_2',
          featureSet: 'product',
          fullPath: 'catalogs/products',
          regionalPageRestrictionKey: 'product',
          regionFallbackPath: 'catalogs/product-upload',
        },
      },
      {
        path: 'product-upload',
        component: ProductUploadPage,
        title: 'Products',
        data: {
          symbol: 'package_2',
          featureSet: 'product',
          fullPath: 'catalogs/product-upload',
          regionalPageRestrictionKey: 'productImport',
          regionFallbackPath: 'catalogs/products',
        },
      },
      {
        path: 'pricing',
        component: PricingPage,
        title: 'Pricing',
        data: {
          symbol: 'sell',
          featureSet: 'pricing',
          regionalPageRestrictionKey: 'pricing',
          fullPath: 'catalogs/pricing',
        },
      },
      {
        path: 'inventory',
        component: InventoryPage,
        title: 'Inventory',
        data: {
          symbol: 'shelves',
          featureSet: 'inventory',
          regionalPageRestrictionKey: 'inventory',
          fullPath: 'catalogs/inventory',
        },
      },
      {
        path: 'sku-mapping',
        component: SkuMappingPage,
        title: 'SKU Mapping',
        canActivate: [authGuard],
        data: {
          symbol: 'map',
          requiredPermission: 'product:manage',
          fullPath: 'catalogs/sku-mapping',
        },
      },
    ],
  },
  {
    path: 'currency-conversion',
    component: CurrencyConversionPage,
    title: 'Currency Conversion',
    data: {
      hideFromNavigation: true,
      fullPath: 'currency-conversion',
    },
    canActivate: [authGuard],
  },
  {
    path: 'download',
    component: DownloadsPage,
    title: 'Downloads',
    data: {
      symbol: 'download',
      featureSet: 'downloads',
      regionalPageRestrictionKey: 'downloads',
    },
    canActivate: [authGuard],
  },
  {
    path: 'locations',
    title: 'Locations',
    data: { symbol: 'travel_explore' },
    canActivate: [authGuard],
    children: [
      {
        path: '', // Empty path matches the parent route
        redirectTo: 'business-entities', // Redirect to 'child1' by default
        pathMatch: 'full', // Ensure a full match for the empty path
      },
      {
        path: 'business-entities',
        component: BusinessEntityPage,
        title: 'Business Entities',
        data: {
          symbol: 'source_environment',
          featureSet: 'businessentity', // Matches the feature as defined in the backend.
          fullPath: 'locations/business-entities',
        },
      },
      {
        path: 'device-care-centers',
        component: DeviceCareCenterPage,
        title: 'Device Care Centers',
        data: {
          symbol: 'devices',
          featureSet: 'dcc',
          fullPath: 'locations/device-care-centers',
        },
      },
      {
        path: 'warehouses',
        component: WarehouseManagementPage,
        title: 'Warehouses',
        data: {
          symbol: 'warehouse',
          featureSet: 'warehouseManagement',
          fullPath: 'locations/warehouses',
        },
      },
      {
        path: 'warehouses/:warehouseCode',
        component: WarehouseDetailsPage,
        title: 'Warehouse Details',
        data: {
          hideFromNavigation: true,
        },
      },
      {
        path: 'warehouses/:warehouseCode/rma-config',
        component: RmaConfigPage,
        title: 'RMA Configuration',
        data: {
          hideFromNavigation: true,
        },
      },
      {
        path: 'warehouse-systems',
        component: WmsPage,
        title: 'Warehouse Systems',
        data: {
          symbol: 'host',
          featureSet: 'wms',
          fullPath: 'locations/warehouse-systems',
        },
      },
    ],
  },
  {
    path: 'buyers',
    title: 'Buyers',
    data: { symbol: 'account_circle' },
    canActivate: [authGuard],
    children: [
      {
        path: '', // Empty path matches the parent route
        redirectTo: 'upload', // Redirect to 'child1' by default
        pathMatch: 'full', // Ensure a full match for the empty path
      },
      {
        path: 'upload',
        component: BuyerUploadPage,
        title: 'Buyer Upload',
        data: {
          featureSet: 'buyer',
          regionalPageRestrictionKey: 'buyerImport',
          fullPath: '/buyers/upload',
        },
      },
      {
        path: 'address-upload',
        component: BuyerAddressUploadPage,
        title: 'Address Upload',
        data: {
          featureSet: 'buyer',
          regionalPageRestrictionKey: 'buyerAddressImport',
          fullPath: '/buyers/address-upload',
        },
      },
    ],
  },
  {
    path: 'rma-management',
    title: 'RMA Management',
    data: { symbol: 'undo', featureSet: 'rma' },
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: 'summary',
        pathMatch: 'full',
      },
      {
        path: 'summary',
        component: RmaSummaryPage,
        title: 'Summary',
        data: {
          symbol: 'summarize',
          featureSet: 'public',
          fullPath: '/rma-management/summary',
        },
      },
      {
        path: 'create',
        component: RmaCreatePage,
        title: 'Create RMA',
        data: {
          hideFromNavigation: true,
        },
      },
      {
        path: 'summary/:rmaId',
        component: RmaDetailsPage,
        title: 'RMA Details',
        data: {
          hideFromNavigation: true,
        },
      },
      {
        path: 'my-approvals',
        title: 'My Approvals',
        data: {
          featureSet: 'public',
          isSectionGroup: true,
        },
        children: [
          {
            path: 'pending',
            component: PendingApprovalPage,
            title: 'Pending',
            data: {
              featureSet: 'public',
              fullPath: '/rma-management/my-approvals/pending',
              showIndicatorDot: true,
              badgeCount: 5,
            },
          },
          {
            path: 'approved-rejected',
            component: ApprovedRejectedPage,
            title: 'Approved & Rejected',
            data: {
              featureSet: 'public',
              fullPath: '/rma-management/my-approvals/approved-rejected',
            },
          },
        ],
      },
    ],
  },
  {
    path: 'user-management',
    component: UserManagementPage,
    title: 'User Management',
    data: { symbol: 'manage_accounts' },
    canActivate: [authGuard],
    children: [
      {
        path: '', // Empty path matches the parent route
        redirectTo: 'users', // Redirect to 'child1' by default
        pathMatch: 'full', // Ensure a full match for the empty path
      },
      {
        path: 'users',
        component: UsersPage,
        title: 'Users',
        data: {
          symbol: 'shield_person',
          fullPath: '/user-management/users',
        },
      },
      {
        path: 'update-user/:userEmail',
        component: UserFormPage,
        title: 'Update User',
        data: {
          hideFromNavigation: true,
        },
      },
      {
        path: 'create-user',
        component: UserFormPage,
        title: 'Create User',
        data: {
          hideFromNavigation: true,
        },
      },
      {
        path: 'buyer-login',
        component: BuyerManagementPage,
        title: 'Buyers',
        data: {
          symbol: 'account_circle',
          fullPath: '/user-management/buyer-login',
        },
      },
      {
        path: 'buyer-login-upload',
        component: BuyerLoginUploadPage,
        title: 'Buyer Login Upload',
        data: {
          symbol: 'upload_file',
          fullPath: '/user-management/buyer-login-upload',
        },
      },
    ],
  },
];
